import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import './AlertSystem.css';

export interface Alert {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number; // in milliseconds, default 5000
  persistent?: boolean; // if true, won't auto-dismiss
}

interface AlertContextType {
  alerts: Alert[];
  addAlert: (alert: Omit<Alert, 'id'>) => void;
  removeAlert: (id: string) => void;
  clearAllAlerts: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlerts = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlerts must be used within an AlertProvider');
  }
  return context;
};

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const addAlert = (alertData: Omit<Alert, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newAlert: Alert = {
      id,
      duration: 5000,
      ...alertData,
    };
    
    setAlerts(prev => [...prev, newAlert]);

    // Auto-remove alert after duration (unless persistent)
    if (!newAlert.persistent && newAlert.duration) {
      setTimeout(() => {
        removeAlert(id);
      }, newAlert.duration);
    }
  };

  const removeAlert = (id: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  };

  const clearAllAlerts = () => {
    setAlerts([]);
  };

  return (
    <AlertContext.Provider value={{ alerts, addAlert, removeAlert, clearAllAlerts }}>
      {children}
      <AlertContainer />
    </AlertContext.Provider>
  );
};

const AlertContainer: React.FC = () => {
  const { alerts } = useAlerts();

  return (
    <div className="alert-container">
      {alerts.map((alert) => (
        <AlertItem key={alert.id} alert={alert} />
      ))}
    </div>
  );
};

interface AlertItemProps {
  alert: Alert;
}

const AlertItem: React.FC<AlertItemProps> = ({ alert }) => {
  const { removeAlert } = useAlerts();
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      removeAlert(alert.id);
    }, 300); // Match the CSS transition duration
  };

  const getIcon = () => {
    switch (alert.type) {
      case 'success':
        return <i className="ri-check-line"></i>;
      case 'error':
        return <i className="ri-error-warning-line"></i>;
      case 'warning':
        return <i className="ri-alert-line"></i>;
      case 'info':
        return <i className="ri-information-line"></i>;
      default:
        return <i className="ri-notification-line"></i>;
    }
  };

  return (
    <div className={`alert-item alert-${alert.type} ${isVisible ? 'visible' : ''} ${isLeaving ? 'leaving' : ''}`}>
      <div className="alert-icon">
        {getIcon()}
      </div>
      <div className="alert-content">
        {alert.title && <div className="alert-title">{alert.title}</div>}
        <div className="alert-message">{alert.message}</div>
      </div>
      <button className="alert-close" onClick={handleClose}>
        <i className="ri-close-line"></i>
      </button>
      {!alert.persistent && alert.duration && (
        <div 
          className="alert-progress" 
          style={{ 
            animationDuration: `${alert.duration}ms`,
            animationPlayState: isLeaving ? 'paused' : 'running'
          }}
        ></div>
      )}
    </div>
  );
};

// Convenience hooks for different alert types
export const useSuccessAlert = () => {
  const { addAlert } = useAlerts();
  return (message: string, title?: string, options?: Partial<Alert>) => {
    addAlert({ type: 'success', message, title, ...options });
  };
};

export const useErrorAlert = () => {
  const { addAlert } = useAlerts();
  return (message: string, title?: string, options?: Partial<Alert>) => {
    addAlert({ type: 'error', message, title, ...options });
  };
};

export const useWarningAlert = () => {
  const { addAlert } = useAlerts();
  return (message: string, title?: string, options?: Partial<Alert>) => {
    addAlert({ type: 'warning', message, title, ...options });
  };
};

export const useInfoAlert = () => {
  const { addAlert } = useAlerts();
  return (message: string, title?: string, options?: Partial<Alert>) => {
    addAlert({ type: 'info', message, title, ...options });
  };
};
